import React, { useState, useRef } from 'react';
import styled from 'styled-components';

/**
 * The six-digit code entry.
 *
 * The boxes used to be 40px squares with a 1px #ccc border and no filled or
 * focused state, so the only way to tell where you were typing was the caret.
 * They now match the fields on the step before: a hairline border, the brand
 * focus ring, and a box that darkens once it holds a digit.
 */

const Boxes = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

const Digit = styled.input`
  flex: 1;
  min-width: 0;
  height: 58px;
  padding: 0;
  text-align: center;
  font-family: inherit;
  font-size: 1.35rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: #0f1b24;
  background: ${({ $filled }) => ($filled ? '#f6fafd' : '#fff')};
  border: 1px solid ${({ $filled }) => ($filled ? '#cfe0ea' : '#e0e6ea')};
  border-radius: 8px;
  box-sizing: border-box;
  transition: border-color 140ms ease, background 140ms ease, box-shadow 140ms ease;

  &:focus {
    outline: none;
    border-color: #1ba8f1;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(27, 168, 241, 0.16);
  }

  /* The numeric keypad on mobile still renders spinners in some browsers. */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  @media screen and (max-width: 400px) {
    height: 52px;
    font-size: 1.15rem;
  }
`;

const OTPInput = ({ length = 6, setOTP }) => {
    const [otpArray, setOtpArray] = useState(new Array(length).fill(''));
    const inputsRef = useRef([]);

    const isInputValueValid = (value) => /^[0-9]*$/.test(value);

    const handleChange = (event, index) => {
      const value = event.target.value;

      // If the input contains the entire OTP, split and populate the fields
      if (value.length === length && isInputValueValid(value)) {
        const newOtp = value.split('').slice(0, length);
        setOtpArray(newOtp);
        setOTP(newOtp.join(''));
        inputsRef.current[length - 1]?.focus();
        return;
      }

      // Handle single character input for regular typing
      if (isInputValueValid(value)) {
        const newOtp = [...otpArray];
        newOtp[index] = value.charAt(0); // Only use the first character
        setOtpArray(newOtp);
        setOTP(newOtp.join(''));

        // Move to the next input field if a valid value is entered
        if (value && index < length - 1) {
          inputsRef.current[index + 1]?.focus();
        }
      }
    };

    const handleKeyDown = (event, index) => {
      if (event.key === 'Backspace') {
        event.preventDefault();
        const newOtp = [...otpArray];
        if (otpArray[index] === '' && index > 0) {
          newOtp[index - 1] = '';
          inputsRef.current[index - 1]?.focus();
        } else {
          newOtp[index] = '';
        }
        setOtpArray(newOtp);
        setOTP(newOtp.join(''));
      }
    };

    const handlePaste = (event) => {
      event.preventDefault();
      const pastedData = event.clipboardData
        .getData('text')
        .replace(/[^0-9]/g, ''); // Sanitize pasted data
      if (pastedData.length === length) {
        const newOtp = pastedData.split('');
        setOtpArray(newOtp);
        setOTP(newOtp.join(''));
        inputsRef.current[length - 1]?.focus();
      }
    };

    return (
      <Boxes onPaste={handlePaste}>
        {otpArray.map((digit, index) => (
          <Digit
            key={index}
            ref={(el) => (inputsRef.current[index] = el)}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            aria-label={`Digit ${index + 1} of ${length}`}
            value={digit}
            $filled={Boolean(digit)}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onFocus={(e) => e.target.select()}
          />
        ))}
      </Boxes>
    );
  };

  export default OTPInput;
